import React from 'react';
import { Flex, Text } from '@mantine/core';
import { HeaderContext } from '@tanstack/react-table';
import { flexRender } from '@dealstack/frontend/shared/tables/flexRender';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { doesFilterHaveValue } from '@dealstack/frontend/shared/components/Table/MantineTable/hooks/useIsFiltersApplied';

interface Props<TData, TValue> {
	context: HeaderContext<TData, TValue>;
	children?: React.ReactNode;
	enableSorting?: boolean;
	headerText?: string;
	headerTextProps?: React.ComponentProps<typeof Text>;
	iconPosition?: 'left' | 'right';
}

export const ColumnHeader = <TData, TValue>({
	context,
	children,
	enableSorting = true,
	headerText,
	headerTextProps = {},
	iconPosition = 'right',
}: Props<TData, TValue>) => {
	const { column } = context;
	const canSort = enableSorting && column.getCanSort();
	const sortDirection = column.getIsSorted();
	const filterValue = column.getFilterValue();
	const isFiltered = doesFilterHaveValue(filterValue);

	const handleSort = () => {
		if (canSort) {
			column.toggleSorting();
		}
	};

	const getSortIcon = () => {
		if (!canSort) {
			return null;
		}

		switch (sortDirection) {
			case 'asc':
				return (
					<Icon variant="arrow-up-v3" size="lg" color={defaultTheme.v3.colors.blue[6]} />
				);
			case 'desc':
				return (
					<Icon
						variant="arrow-down-v3"
						size="lg"
						color={defaultTheme.v3.colors.blue[6]}
					/>
				);
			default:
				return null;
		}
	};

	const getFilterIcon = () => {
		if (!isFiltered) {
			return null;
		}

		return <Icon variant="filter-v3" size="lg" color={defaultTheme.v3.colors.blue[6]} />;
	};

	const headerContent =
		children ||
		(headerText ? (
			<Text fw={600} {...headerTextProps}>
				{headerText}
			</Text>
		) : (
			flexRender(column.columnDef.header, context)
		));

	const renderIcon = () => {
		const sortIcon = getSortIcon();
		const filterIcon = getFilterIcon();

		if (!sortIcon && !filterIcon) {
			return null;
		}

		return (
			<Flex align="center" gap="4px">
				{filterIcon}
				{sortIcon}
			</Flex>
		);
	};

	const renderContent = () => {
		const hasIcons = canSort || isFiltered;

		if (iconPosition === 'left') {
			return (
				<>
					{renderIcon()}
					<Flex
						align="center"
						style={{ flex: 1, minWidth: 0, marginLeft: hasIcons ? '8px' : 0 }}
					>
						{headerContent}
					</Flex>
				</>
			);
		} else {
			return (
				<>
					<Flex align="center" style={{ flex: 1, minWidth: 0 }}>
						{headerContent}
					</Flex>
					{renderIcon()}
				</>
			);
		}
	};

	return (
		<Flex
			align="center"
			justify="space-between"
			w="100%"
			style={{
				cursor: canSort ? 'pointer' : 'default',
				userSelect: 'none',
			}}
			onClick={handleSort}
		>
			{renderContent()}
		</Flex>
	);
};

export const createSortableTextHeader = <TData = unknown, TValue = unknown>(
	text: string,
	enableSorting = true,
	textProps?: React.ComponentProps<typeof Text>,
	iconPosition: 'left' | 'right' = 'right'
) => {
	return (context: HeaderContext<TData, TValue>) => (
		<ColumnHeader
			context={context}
			headerText={text}
			enableSorting={enableSorting}
			headerTextProps={textProps}
			iconPosition={iconPosition}
		/>
	);
};

/** Right-aligned header for numeric columns; the sort icon sits left so the label stays flush with the values. */
export const createNumericHeader = <TData = unknown, TValue = unknown>(
	text: string,
	enableSorting = true
) =>
	createSortableTextHeader<TData, TValue>(
		text,
		enableSorting,
		{
			style: {
				width: '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'flex-end',
				textAlign: 'right',
			},
		},
		'left'
	);
